import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useSchools, useReviews } from "../../hooks/useSchools";
import { useReviews as useReviewsHook } from "../../hooks/useReviews";
import { useAuthStore } from "../../store/authStore";
import ReviewForm from "../reviews/ReviewForm";
import CommentsList from "../comments/CommentsList";
import ReviewCard from "../../components/ReviewCard";
import RatingStars from "../../components/RatingStars";
import Pagination from "../../components/Pagination";

export default function SchoolDetail() {
  const { id } = useParams();
  const { getSchool } = useSchools();
  const { fetchReviews } = useReviews();
  const { checkUserReview } = useReviewsHook();
  const { isAuthenticated, user } = useAuthStore();

  const [school, setSchool] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [userReview, setUserReview] = useState(null);
  const [checkingReview, setCheckingReview] = useState(false);

  // Thêm states cho phân trang
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReviews, setTotalReviews] = useState(0);
  const REVIEWS_PER_PAGE = 5;

  useEffect(() => {
    if (id) {
      loadSchoolData();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (id && isAuthenticated) {
      checkUserAlreadyReviewed();
    }
  }, [id, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load reviews khi currentPage thay đổi
  useEffect(() => {
    if (id && !loading) {
      loadReviews();
    }
  }, [currentPage, id, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSchoolData = async () => {
    setLoading(true);
    setError("");

    try {
      const schoolResult = await getSchool(id);

      if (schoolResult && schoolResult.success !== false) {
        setSchool(schoolResult);
        // Load reviews sau khi có school data
        await loadReviews();
      } else {
        setError(schoolResult?.error || "Không tìm thấy trường học");
      }
    } catch (err) {
      console.error("Lỗi khi tải dữ liệu:", err);
      setError("Đã xảy ra lỗi khi tải dữ liệu");
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    setReviewsLoading(true);
    try {
      const reviewsResult = await fetchReviews({
        school: id,
        page: currentPage,
        limit: REVIEWS_PER_PAGE,
      });

      if (reviewsResult && reviewsResult.success !== false) {
        setReviews(
          reviewsResult?.reviews || reviewsResult?.data?.reviews || []
        );
        setTotalReviews(
          reviewsResult?.total || reviewsResult?.data?.total || 0
        );
        setTotalPages(
          reviewsResult?.totalPages || reviewsResult?.data?.totalPages || 1
        );
        setCurrentPage(
          reviewsResult?.currentPage || reviewsResult?.data?.currentPage || 1
        );
      }
    } catch (err) {
      console.error("Lỗi khi tải reviews:", err);
    } finally {
      setReviewsLoading(false);
    }
  };

  const checkUserAlreadyReviewed = async () => {
    setCheckingReview(true);
    try {
      const result = await checkUserReview(id);
      if (result.success && result.data) {
        setUserReview(result.data.review);
      }
    } catch (err) {
      console.error("Lỗi khi kiểm tra review:", err);
    } finally {
      setCheckingReview(false);
    }
  };

  const handleReviewSuccess = () => {
    setShowReviewForm(false);
    loadSchoolData(); // Reload data
    checkUserAlreadyReviewed(); // Check again
  };

  // Xử lý cập nhật review sau khi đánh dấu hữu ích
  const handleReviewUpdate = (reviewId, updatedReview) => {
    setReviews(
      reviews.map((review) =>
        (review._id || review.id) === reviewId
          ? { ...review, ...updatedReview }
          : review
      )
    );
  };

  const handleViewMyReview = () => {
    if (userReview) {
      window.location.href = `/reviews/${userReview._id || userReview.id}`;
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getReviewId = (review) => {
    // Backend có thể trả về id hoặc _id
    return review._id || review.id;
  };

  if (loading) {
    return <div className="school-detail__loading">Đang tải...</div>;
  }

  if (error) {
    return <div className="school-detail__error">{error}</div>;
  }

  if (!school) {
    return (
      <div className="school-detail__not-found">Không tìm thấy trường học</div>
    );
  }

  const renderReviewButton = () => {
    if (!isAuthenticated) {
      return (
        <Link to="/login" className="btn btn--primary">
          Đăng nhập để viết đánh giá
        </Link>
      );
    }

    if (checkingReview) {
      return (
        <button className="btn btn--secondary" disabled>
          Đang kiểm tra...
        </button>
      );
    }

    if (userReview) {
      return (
        <div className="school-detail__review-status">
          <button
            className="btn btn--secondary btn--reviewed"
            onClick={handleViewMyReview}
            disabled
          >
            <span className="btn__icon">✓</span>
            Bạn đã đánh giá trường này
          </button>
          <p className="school-detail__review-note">
            Bạn chỉ có thể đánh giá mỗi trường một lần.{" "}
            <button onClick={handleViewMyReview} className="link-button">
              Xem đánh giá của bạn
            </button>
          </p>
        </div>
      );
    }

    return (
      <button
        onClick={() => setShowReviewForm(true)}
        className="btn btn--primary"
      >
        <span className="btn__icon">📝</span>
        Viết đánh giá
      </button>
    );
  };

  return (
    <div className="school-detail">
      <div className="school-detail__container">
        {/* School Header */}
        <div className="school-detail__header">
          {school?.image && (
            <img
              src={school.image}
              alt={school.name}
              className="school-detail__image"
            />
          )}

          <div className="school-detail__info">
            <div className="school-detail__title-section">
              <h1 className="school-detail__name">{school?.name}</h1>
              {school?.short_name && (
                <span className="school-detail__short-name">
                  ({school.short_name})
                </span>
              )}
              <span className="school-detail__level">{school?.level}</span>
            </div>

            {school?.code_university && (
              <p className="school-detail__code">
                Mã trường: {school.code_university}
              </p>
            )}

            <p className="school-detail__address">{school?.address}</p>

            {school?.description && (
              <p className="school-detail__description">{school.description}</p>
            )}

            {/* Contact Info */}
            {school?.contact && Object.keys(school.contact).length > 0 && (
              <div className="school-detail__contact">
                <h3>Thông tin liên hệ</h3>
                {school.contact.phone && <p>📞 {school.contact.phone}</p>}
                {school.contact.email && <p>📧 {school.contact.email}</p>}
                {school.contact.website && (
                  <p>
                    🌐{" "}
                    <a
                      href={school.contact.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {school.contact.website}
                    </a>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Ratings Overview */}
        {school?.ratings && (
          <div className="school-detail__ratings">
            <h2>Đánh giá tổng quan</h2>
            <div className="school-detail__ratings-grid">
              <div className="school-detail-rating-card school-detail-rating-card--overall">
                <h3>Tổng thể</h3>
                <div className="school-detail-rating-card__stars">
                  <RatingStars
                    rating={school.ratings.overall || 0}
                    integerOnly={false}
                  />
                </div>
                <span className="school-detail-rating-card__score">
                  {(school.ratings.overall || 0).toFixed(1)}/5
                </span>
              </div>

              <div className="school-detail-rating-card">
                <h4>Chất lượng giảng dạy</h4>
                <div className="school-detail-rating-card__stars">
                  <RatingStars rating={school.ratings.teaching || 0} />
                </div>
                <span className="school-detail-rating-card__score">
                  {school.ratings.teaching || 0}/5
                </span>
              </div>

              <div className="school-detail-rating-card">
                <h4>Cơ sở vật chất</h4>
                <div className="school-detail-rating-card__stars">
                  <RatingStars rating={school.ratings.facilities || 0} />
                </div>
                <span className="school-detail-rating-card__score">
                  {school.ratings.facilities || 0}/5
                </span>
              </div>

              <div className="school-detail-rating-card">
                <h4>Hoạt động ngoại khóa</h4>
                <div className="school-detail-rating-card__stars">
                  <RatingStars rating={school.ratings.activities || 0} />
                </div>
                <span className="school-detail-rating-card__score">
                  {school.ratings.activities || 0}/5
                </span>
              </div>

              <div className="school-detail-rating-card">
                <h4>Môi trường học tập</h4>
                <div className="school-detail-rating-card__stars">
                  <RatingStars rating={school.ratings.environment || 0} />
                </div>
                <span className="school-detail-rating-card__score">
                  {school.ratings.environment || 0}/5
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Reviews Section */}
        <div className="school-detail__reviews">
          <div className="school-detail__reviews-header">
            <h2>Đánh giá ({totalReviews})</h2>
            {renderReviewButton()}
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <div className="school-detail__review-form">
              <ReviewForm
                school={school}
                onSuccess={handleReviewSuccess}
                onCancel={() => setShowReviewForm(false)}
              />
            </div>
          )}

          {/* Reviews List */}
          {reviewsLoading ? (
            <div className="school-detail__reviews-loading">
              <div className="loading-spinner"></div>
              <p>Đang tải đánh giá...</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="school-detail__no-reviews">
              <p>Chưa có đánh giá nào cho trường này.</p>
              {!isAuthenticated && (
                <p>
                  <Link to="/login">Đăng nhập</Link> để viết đánh giá đầu tiên!
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Reviews stats */}
              <div className="school-detail__reviews-stats">
                <p>
                  Hiển thị {(currentPage - 1) * REVIEWS_PER_PAGE + 1} -{" "}
                  {Math.min(currentPage * REVIEWS_PER_PAGE, totalReviews)} của{" "}
                  {totalReviews} đánh giá
                </p>
              </div>

              <div className="school-detail__reviews-list">
                {reviews.map((review) => {
                  const reviewId = review._id || review.id;
                  return (
                    <div key={reviewId} className="school-detail__review-item">
                      <ReviewCard
                        review={{
                          ...review,
                          id: reviewId,
                          school: school,
                        }}
                        onReviewUpdate={handleReviewUpdate}
                      />

                      {/* Comments toggle */}
                      <div className="school-detail__review-actions">
                        <button
                          onClick={() =>
                            setSelectedReview(
                              selectedReview === reviewId ? null : reviewId
                            )
                          }
                          className="btn btn--link"
                        >
                          {selectedReview === reviewId
                            ? "Ẩn bình luận"
                            : "Xem bình luận"}
                        </button>
                      </div>

                      {/* Comments */}
                      {selectedReview === reviewId && (
                        <div className="school-detail__comments-section">
                          <CommentsList reviewId={reviewId} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalReviews}
                onPageChange={handlePageChange}
                itemName="đánh giá"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
